import qs from 'query-string';
import http from 'httpplease';

function hash(method, url, { data, headers }) {
    return JSON.stringify({ method, url, data, headers });
}

function stringifyQS(data) {
    data = { ...data };

    for (let key of Object.keys(data)) {
        if (data[key] && typeof data[key] === 'object' && !Array.isArray(data[key])) {
            for (let inner of Object.keys(data[key])) {
                data[key + '[' + inner + ']'] = data[key][inner];
            }

            delete data[key];
        }
    }

    return qs.stringify(data);
}

let requests = {};
let temporaryRequests = {};

function eventuallyJSON(x) {
    try {
        return JSON.parse(x);
    } catch (e) {
        return x;
    }
}

class Api {
    constructor(url) {
        this.url = url;
        this.observers = [];
    }

    request(method, url, { data, headers: reqHeaders, cache, id: requestId, isStream } = {}) {
        url = this.url + url;
        const headers = Object.assign( {},
            {
                'Accept': 'application/json'
            },
            isStream ? null : { 'Content-Type': 'application/json' },
            reqHeaders
        );

        method = method.toUpperCase();

        if (method === 'GET') {
            if (Object.keys(data || {}).length) {
                url += url.indexOf('?') === -1 ? '?' : '&';
                url += stringifyQS(data);
            }

            data = undefined;
        }

        let id = hash(method, url, { data, headers });

        cache = +cache;

        if (cache < 100) {
            cache = 100;
        }

        let isCached = cache && requests[id] && requests[id].expires >= Date.now();

        if (isCached) {
            let promise = requests[id].promise;

            if (promise.response) {
                return Promise.resolve().then(() => {
                    if (promise.response.err) {
                        throw promise.response.err;
                    }

                    return promise.response.res;
                });
            }

            return requests[id].promise;
        }

        if (requestId != null && temporaryRequests[requestId]) {
            temporaryRequests[requestId].abort();
        }

        let request;
        let promise = new Promise((resolve, reject) => {
            request = http({
                url,
                method,
                body: typeof data === 'object' && !isStream ? JSON.stringify(data) : data,
                headers
            }, (err, res) => {
                let response = res;

                if (res) {
                    res = eventuallyJSON(res.body);
                }

                if (err) {
                    err = eventuallyJSON(err.body);
                }

                promise.response = { err, res };

                if (promise.aborted) {
                    return;
                }

                if (requestId) {
                    delete temporaryRequests[requestId];
                }

                this.observers.forEach(x => x(response, res, err));

                if (err) {
                    reject(err);
                } else {
                    resolve(res);
                }
            });
        });

        if (cache) {
            requests[id] = {
                expires: Date.now() + cache,
                promise
            };
        }

        if (requestId) {
            temporaryRequests[requestId] = promise;
        }

        promise.abort = () => {
            promise.aborted = true;
            request.abort();
        };

        return promise;
    }

    'get'(url, data, options) {
        return this.request('GET', url, { ...options, data });
    }

    del(url, data, options) {
        return this.request('DELETE', url, { ...options, data });
    }

    post(url, data, options) {
        return this.request('POST', url, { ...options, data });
    }

    postUpload( url, { data } ) {
        return this.request( 'POST', url, { data, isStream: true } );
    }

    put(url, data, options) {
        return this.request('PUT', url, { ...options, data });
    }

    patch(url, data, options) {
        return this.request('PATCH', url, { ...options, data });
    }

    addObserver(observer) {
        this.observers.push(observer);
    }

    clone(observers) {
        if (observers === undefined) {
            observers = true;
        }

        let api = new Api(this.url);

        if (observers) {
            api.observers = this.observers;
        }

        return api;
    }
}

Api.FOREVER = Api.prototype.FOREVER = 30 * 24 * 3600000;

export default Api;
